import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Store, Plus, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Header shop switcher — shows current shop + dropdown to switch or add new.
 */
export default function ShopSwitcher() {
  const { t, user, shops, activeShop, switchShop, addShop } = useApp();
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const isOwner = user?.role === "owner";

  const pick = async (sid) => {
    if (sid === user?.active_shop_id) { setOpen(false); return; }
    try { await switchShop(sid); toast.success("Switched"); setOpen(false); }
    catch (err) { toast.error("Failed"); }
  };

  const save = async () => {
    if (!name.trim()) return;
    try {
      const s = await addShop(name.trim());
      await switchShop(s.id);
      toast.success("Shop added");
      setAddOpen(false); setName("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!activeShop) return null;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="shop-switcher-btn"
              className="h-10 px-3 rounded-xl bg-[#FFF7ED] border border-amber-200 flex items-center gap-2 text-sm font-medium text-[#B45309] max-w-[200px]">
        <Store className="w-4 h-4 shrink-0" />
        <span className="truncate">{activeShop.name}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("switch_shop")}</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {shops.map((s) => (
              <button key={s.id} onClick={() => pick(s.id)} data-testid={`shop-pick-${s.id}`}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-left ${s.id === user?.active_shop_id ? "bg-[#FFF7ED] border border-amber-200" : "hover:bg-stone-50"}`}>
                <span className="font-medium">{s.name}</span>
                {s.id === user?.active_shop_id && <Check className="w-4 h-4 text-[#B45309]" />}
              </button>
            ))}
          </div>
          {isOwner && (
            <Button onClick={() => { setOpen(false); setAddOpen(true); }} data-testid="shop-add-open"
                    className="w-full h-11 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold mt-2">
              <Plus className="w-4 h-4 mr-2" /> {t("add_shop")}
            </Button>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("add_shop")}</DialogTitle></DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("shop_name")} data-testid="shop-add-name" className="h-12 rounded-lg" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setAddOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="shop-add-save" className="bg-[#B45309] hover:bg-[#92400E] text-white">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
